// Crosshair canvas, hit markers and the perfect-hop pulse (all centered on the screen).

import { drawCrosshair, drawDotReticle } from "../settings/crosshair";
import type { Settings } from "../settings/schema";
import { defaultSettings } from "../settings/schema";
import { div, play } from "./dom";
import { spreadToPixels } from "./format";

export type HitKind = "body" | "head" | "kill";

/** CSS size of the (square) crosshair canvas. The widest dynamic crosshair is clamped to fit. */
const CANVAS_CSS = 512;
const MAX_EXTENT = CANVAS_CSS / 2 - 6;

const HIT_SVG =
  '<svg viewBox="-24 -24 48 48" fill="none" stroke-linecap="round" aria-hidden="true" focusable="false">' +
  '<circle class="hud-hit__ring" r="17"/>' +
  '<path class="hud-hit__ticks" d="M-7 -7L-14 -14M7 -7L14 -14M-7 7L-14 14M7 7L14 14"/></svg>';

interface HitAnim {
  frames: Keyframe[];
  opts: KeyframeAnimationOptions;
}

const HIT_ANIMS: Readonly<Record<HitKind, HitAnim>> = {
  body: {
    frames: [
      { opacity: 1, transform: "translate(-50%, -50%) scale(0.8)" },
      { opacity: 1, transform: "translate(-50%, -50%) scale(1)", offset: 0.2 },
      { opacity: 0, transform: "translate(-50%, -50%) scale(1.12)" },
    ],
    opts: { duration: 240, easing: "ease-out" },
  },
  head: {
    frames: [
      { opacity: 1, transform: "translate(-50%, -50%) scale(0.7)" },
      { opacity: 1, transform: "translate(-50%, -50%) scale(1.05)", offset: 0.18 },
      { opacity: 1, transform: "translate(-50%, -50%) scale(1)", offset: 0.55 },
      { opacity: 0, transform: "translate(-50%, -50%) scale(1.2)" },
    ],
    opts: { duration: 420, easing: "ease-out" },
  },
  kill: {
    frames: [
      { opacity: 1, transform: "translate(-50%, -50%) scale(0.6) rotate(-12deg)" },
      { opacity: 1, transform: "translate(-50%, -50%) scale(1.25) rotate(0deg)", offset: 0.2 },
      { opacity: 1, transform: "translate(-50%, -50%) scale(1.1)", offset: 0.6 },
      { opacity: 0, transform: "translate(-50%, -50%) scale(1.45)" },
    ],
    opts: { duration: 560, easing: "ease-out" },
  },
};

const HOP_FRAMES: Keyframe[] = [
  { opacity: 0.95, transform: "translate(-50%, -50%) scale(0.55)" },
  { opacity: 0, transform: "translate(-50%, -50%) scale(1.7)" },
];
const HOP_OPTS: KeyframeAnimationOptions = { duration: 320, easing: "cubic-bezier(0.2, 0.7, 0.2, 1)" };

export class CrosshairLayer {
  readonly root = div("hud-center");
  private readonly canvas = document.createElement("canvas");
  private readonly ctx: CanvasRenderingContext2D | null;
  private readonly hit = div("hud-hit");
  private readonly hop = div("hud-hop");

  private settings: Settings = defaultSettings();
  private viewportH = 1080;
  private viewportW = 1920;
  private dpr = 1;
  // draw cache
  private drawn = false;
  private shown = false;
  private gapKey = -1;
  private crosshairRef: Settings["crosshair"] | null = null;
  private scaleKey = 0;
  private dotKey = false;

  constructor() {
    this.canvas.className = "hud-xhair";
    this.canvas.style.width = `${CANVAS_CSS}px`;
    this.canvas.style.height = `${CANVAS_CSS}px`;
    this.ctx = this.canvas.getContext("2d");
    this.hit.innerHTML = HIT_SVG;
    this.hit.style.opacity = "0";
    this.hop.style.opacity = "0";
    this.root.append(this.canvas, this.hit, this.hop);
    this.resizeCanvas();
  }

  applySettings(s: Settings): void {
    this.settings = s;
  }

  setViewport(w: number, h: number, dpr: number): void {
    if (h > 0) this.viewportH = h;
    if (w > 0) this.viewportW = w;
    if (dpr !== this.dpr) {
      this.dpr = dpr;
      this.resizeCanvas();
    }
  }

  /**
   * Redraw only when the crosshair look, spread bucket (0.5 px), scale or visibility changed.
   * `dot`: aiming through a dot sight, so a crisp dot replaces the crosshair (spread is not drawn).
   */
  update(show: boolean, spreadDeg: number, hFovDeg: number, dot = false): void {
    const c = this.settings.crosshair;
    const scale = this.settings.hud.scale;
    let gap = 0;
    if (show && c.dynamic && !dot) {
      const px = spreadToPixels(spreadDeg, hFovDeg, this.viewportH, this.viewportW / this.viewportH);
      const room = MAX_EXTENT / scale - c.gap - c.size;
      gap = Math.round(Math.min(px, Math.max(0, room)) * 2) / 2;
    }
    if (this.drawn && show === this.shown && gap === this.gapKey && c === this.crosshairRef && scale === this.scaleKey && dot === this.dotKey) return;
    this.drawn = true;
    this.shown = show;
    this.gapKey = gap;
    this.crosshairRef = c;
    this.scaleKey = scale;
    this.dotKey = dot;
    const ctx = this.ctx;
    if (!ctx) return;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, CANVAS_CSS, CANVAS_CSS);
    if (show) {
      if (dot) drawDotReticle(ctx, c, CANVAS_CSS / 2, CANVAS_CSS / 2, scale);
      else drawCrosshair(ctx, c, CANVAS_CSS / 2, CANVAS_CSS / 2, gap, scale);
    }
  }

  hitmarker(kind: HitKind): void {
    if (!this.settings.hud.hitmarkers) return;
    this.hit.className = `hud-hit hud-hit--${kind}`;
    const a = HIT_ANIMS[kind];
    play(this.hit, a.frames, a.opts);
  }

  perfectHop(): void {
    play(this.hop, HOP_FRAMES, HOP_OPTS);
  }

  /** Cancel running animations (death, scene change). */
  clear(): void {
    for (const a of this.hit.getAnimations()) a.cancel();
    for (const a of this.hop.getAnimations()) a.cancel();
  }

  private resizeCanvas(): void {
    const px = Math.max(1, Math.round(CANVAS_CSS * this.dpr));
    this.canvas.width = px;
    this.canvas.height = px;
    this.drawn = false;
  }
}
